// cSpell:ignore xyflow
import type { ReactNode } from "react";
import { Handle } from "@xyflow/react";
import type { LucideIcon } from "lucide-react/dist/lucide-react.d.ts";

import { NodeEditButton } from "./NodeEditButton";
import { NODE_DEFAULT_WIDTH } from "../utils/diagramConfig";
import { useDiagramHandlePositions } from "../hooks/useDiagramHandlePositions";
import { useTheme } from "@/hooks/useTheme";

export type NodeVariant =
  | "exclusions"
  | "conditions"
  | "bonus-if"
  | "bonus-else"
  | "bonus-main"
  | "noBonus";

interface VariantStyles {
  /** Bandeau coloré du header (fond + texte). */
  header: string;
  /** Bordure de la carte (correspond au header). */
  border: string;
  /** Liseré supérieur en accent (2px). */
  accent: string;
  /** Couleur des handles ReactFlow. */
  handle: string;
  /** Couleur de l'icône dans le header (sur fond coloré). */
  headerIcon: string;
  /** Teinte du bouton "Modifier" au survol — assortie à la variante. */
  footerHover: string;
  /** Glow box-shadow : [light, dark] en rgba. */
  glow: [string, string];
}

const VARIANTS: Record<NodeVariant, VariantStyles> = {
  exclusions: {
    header: "bg-rose-600 text-white dark:bg-rose-500",
    border: "border-rose-600 dark:border-rose-500",
    accent: "bg-rose-700 dark:bg-rose-400",
    handle: "bg-rose-600!",
    headerIcon: "text-white",
    footerHover:
      "hover:text-rose-700 hover:bg-rose-50 dark:hover:text-rose-300 dark:hover:bg-rose-950/40",
    glow: [
      "0 0 0 1px rgba(225,29,72,0.15), 0 4px 24px -4px rgba(225,29,72,0.18)",
      "0 0 0 1px rgba(251,113,133,0.2), 0 4px 32px -4px rgba(251,113,133,0.35)",
    ],
  },
  conditions: {
    header: "bg-amber-500 text-white dark:bg-amber-500 dark:text-white",
    border: "border-amber-500 dark:border-amber-500",
    accent: "bg-amber-600 dark:bg-amber-400",
    handle: "bg-amber-500!",
    headerIcon: "text-white",
    footerHover:
      "hover:text-amber-700 hover:bg-amber-50 dark:hover:text-amber-300 dark:hover:bg-amber-950/40",
    glow: [
      "0 0 0 1px rgba(245,158,11,0.15), 0 4px 24px -4px rgba(245,158,11,0.2)",
      "0 0 0 1px rgba(251,191,36,0.2), 0 4px 32px -4px rgba(251,191,36,0.38)",
    ],
  },
  "bonus-if": {
    header: "bg-emerald-600 text-white dark:bg-emerald-500",
    border: "border-emerald-600 dark:border-emerald-500",
    accent: "bg-emerald-700 dark:bg-emerald-400",
    handle: "bg-emerald-600!",
    headerIcon: "text-white",
    footerHover:
      "hover:text-emerald-700 hover:bg-emerald-50 dark:hover:text-emerald-300 dark:hover:bg-emerald-950/40",
    glow: [
      "0 0 0 1px rgba(5,150,105,0.15), 0 4px 24px -4px rgba(5,150,105,0.2)",
      "0 0 0 1px rgba(52,211,153,0.2), 0 4px 32px -4px rgba(52,211,153,0.35)",
    ],
  },
  "bonus-else": {
    header: "bg-orange-600 text-white dark:bg-orange-500",
    border: "border-orange-600 dark:border-orange-500",
    accent: "bg-orange-700 dark:bg-orange-400",
    handle: "bg-orange-600!",
    headerIcon: "text-white",
    footerHover:
      "hover:text-orange-700 hover:bg-orange-50 dark:hover:text-orange-300 dark:hover:bg-orange-950/40",
    glow: [
      "0 0 0 1px rgba(234,88,12,0.15), 0 4px 24px -4px rgba(234,88,12,0.2)",
      "0 0 0 1px rgba(251,146,60,0.2), 0 4px 32px -4px rgba(251,146,60,0.35)",
    ],
  },
  "bonus-main": {
    header: "bg-sky-600 text-white dark:bg-sky-500",
    border: "border-sky-600 dark:border-sky-500",
    accent: "bg-sky-700 dark:bg-sky-400",
    handle: "bg-sky-600!",
    headerIcon: "text-white",
    footerHover:
      "hover:text-sky-700 hover:bg-sky-50 dark:hover:text-sky-300 dark:hover:bg-sky-950/40",
    glow: [
      "0 0 0 1px rgba(2,132,199,0.15), 0 4px 24px -4px rgba(2,132,199,0.2)",
      "0 0 0 1px rgba(56,189,248,0.2), 0 4px 32px -4px rgba(56,189,248,0.35)",
    ],
  },
  noBonus: {
    header: "bg-zinc-700 text-white dark:bg-zinc-600",
    border: "border-zinc-700 dark:border-zinc-600",
    accent: "bg-zinc-800 dark:bg-zinc-500",
    handle: "bg-zinc-600!",
    headerIcon: "text-white",
    footerHover:
      "hover:text-zinc-700 hover:bg-zinc-50 dark:hover:text-zinc-300 dark:hover:bg-zinc-900",
    glow: [
      "0 0 0 1px rgba(63,63,70,0.12), 0 4px 20px -4px rgba(63,63,70,0.15)",
      "0 0 0 1px rgba(161,161,170,0.15), 0 4px 24px -4px rgba(161,161,170,0.2)",
    ],
  },
};

interface NodeShellProps {
  variant: NodeVariant;
  icon: LucideIcon;
  title: string;
  /** Index 0-based vers l'étape du wizard à éditer (Redux). Si défini, affiche le bouton d'édition. */
  targetStep?: number;
  /** Cache le handle "target" (entrée). À utiliser pour les nodes racine. */
  hideTopHandle?: boolean;
  /** Cache le handle "source" (sortie). À utiliser pour les nodes terminaux. */
  hideBottomHandle?: boolean;
  /** Cache le bandeau du header — utile pour des nodes ultra-compacts. */
  hideHeader?: boolean;
  /** Largeur explicite (px). Par défaut NODE_DEFAULT_WIDTH. */
  width?: number;
  children?: ReactNode;
}

export function NodeShell({
  variant,
  icon: Icon,
  title,
  targetStep,
  hideTopHandle,
  hideBottomHandle,
  hideHeader,
  width = NODE_DEFAULT_WIDTH,
  children,
}: NodeShellProps) {
  const v = VARIANTS[variant];
  const { source: sourcePos, target: targetPos } = useDiagramHandlePositions();
  const { isDark } = useTheme();
  const glowShadow = isDark ? v.glow[1] : v.glow[0];

  return (
    <div
      style={{ width, boxShadow: glowShadow }}
      className={[
        "relative rounded-lg",
        "border-2",
        v.border,
        "bg-white dark:bg-zinc-950",
        "text-zinc-900 dark:text-zinc-50",
      ].join(" ")}
    >
      {!hideTopHandle && (
        <Handle
          type="target"
          position={targetPos}
          className={`${v.handle} w-2.5! h-2.5! border-2! border-white! dark:border-zinc-950!`}
        />
      )}

      {/* Liseré d'accent en haut, juste sous le handle. Donne du caractère sans déstabiliser. */}
      <div className={`h-1 rounded-t-3xl ${v.accent}`} aria-hidden="true" />

      {!hideHeader && (
        <header
          className={[
            "flex items-center gap-3 min-w-0",
            "px-3 py-2",
            v.header,
          ].join(" ")}
        >
          <Icon
            className={` shrink-0 ${v.headerIcon}`}
            size={32}
            aria-hidden="true"
          />
          <h3 className="min-w-0 flex-1 text-[18px] font-semibold tracking-tight leading-snug wrap-break-word line-clamp-2 text-left">
            {title}
          </h3>
        </header>
      )}

      <div
        className={[
          "px-3 py-3 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 text-xs",
          targetStep === undefined ? "rounded-b-lg" : "",
        ].join(" ")}
      >
        {children}
      </div>

      {targetStep !== undefined && (
        <footer className="relative z-20 pointer-events-auto border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 rounded-b-md overflow-hidden">
          <NodeEditButton targetStep={targetStep} className={v.footerHover} />
        </footer>
      )}

      {!hideBottomHandle && (
        <Handle
          type="source"
          position={sourcePos}
          className={`${v.handle} w-2.5! h-2.5! border-2! border-white! dark:border-zinc-950! z-100`}
        />
      )}
    </div>
  );
}
